import { createFileRoute, Outlet, Link } from "@tanstack/react-router"
import { useAuth, useClerk, useUser } from "@clerk/clerk-react"
import { useEffect, useMemo, useRef } from "react"
import { motion, useReducedMotion } from "framer-motion"
import { useMutation } from "convex/react"
import { api } from "../../convex/_generated/api"
import { usePostHogIdentify, resetUser as resetPostHog } from "@/lib/analytics/posthog"
import { setUser as setSentryUser } from "@/lib/analytics/sentry"
import {
  buildPlayerDisplayName,
  normalizeFirstName,
  normalizeLastInitial,
} from "@/lib/playerName"
import { ThemeToggle, WildGuessLogo } from "@/lib/theme"

interface SessionUser {
  userId: string
  email: string | null
  phoneNumber: string | null
  firstName: string | null
  lastName: string | null
  imageUrl: string | null
}

export const Route = createFileRoute("/_authed")({
  component: AuthedLayout,
})

function AuthedLayout() {
  const { isLoaded, isSignedIn } = useAuth()
  const { user, isLoaded: userLoaded } = useUser()
  const clerk = useClerk()
  const syncCurrentUser = useMutation(api.auth.syncCurrentUser)
  const syncedRef = useRef<string | null>(null)
  const reduced = useReducedMotion()

  useEffect(() => {
    if (!isLoaded) return
    if (isSignedIn) return
    const path = `${window.location.pathname}${window.location.search}`
    window.location.href = `/sign-in?redirect=${encodeURIComponent(path)}`
  }, [isLoaded, isSignedIn])

  const sessionUser = useMemo<SessionUser | null>(() => {
    if (!user) return null
    const email =
      user.primaryEmailAddress?.emailAddress ??
      user.emailAddresses.at(0)?.emailAddress ??
      null
    const phoneNumber =
      user.primaryPhoneNumber?.phoneNumber ??
      user.phoneNumbers.at(0)?.phoneNumber ??
      null

    return {
      userId: user.id,
      email,
      phoneNumber,
      firstName: user.firstName,
      lastName: user.lastName,
      imageUrl: user.imageUrl,
    }
  }, [user])

  useEffect(() => {
    if (!sessionUser) {
      syncedRef.current = null
      return
    }

    const displayName =
      buildPlayerDisplayName({
        firstName: sessionUser.firstName,
        lastInitial: sessionUser.lastName,
      }) ||
      sessionUser.phoneNumber ||
      sessionUser.email ||
      undefined
    const syncKey = `${sessionUser.userId}:${sessionUser.email ?? ""}:${sessionUser.phoneNumber ?? ""}:${displayName ?? ""}`
    if (syncedRef.current === syncKey) return
    syncedRef.current = syncKey

    syncCurrentUser({
      email: sessionUser.email ?? undefined,
      phoneNumber: sessionUser.phoneNumber ?? undefined,
      displayName,
    }).catch((err) => {
      syncedRef.current = null
      const message = err instanceof Error ? err.message : String(err)
      if (message.toLowerCase().includes("closed beta")) {
        window.location.href = "/closed-beta"
        return
      }
      console.error("Failed to sync user:", err)
    })
  }, [sessionUser, syncCurrentUser])

  usePostHogIdentify(sessionUser)
  useEffect(() => {
    if (sessionUser) {
      const sentryName = buildPlayerDisplayName({
        firstName: sessionUser.firstName,
        lastInitial: sessionUser.lastName,
      })
      setSentryUser({
        id: sessionUser.userId,
        email: sessionUser.email ?? undefined,
        name: sentryName,
      })
    }
  }, [sessionUser])

  if (!isLoaded || !userLoaded || !sessionUser) {
    return (
      <div className="console-texture relative flex min-h-screen items-center justify-center">
        <p className="console-kicker">
          Loading...
        </p>
      </div>
    )
  }

  async function handleLogout() {
    resetPostHog()
    setSentryUser(null)
    await clerk.signOut({ redirectUrl: "/" })
  }

  const displayName =
    buildPlayerDisplayName({
      firstName: normalizeFirstName(sessionUser.firstName),
      lastInitial: normalizeLastInitial(sessionUser.lastName),
    }) ||
    sessionUser.phoneNumber ||
    sessionUser.email ||
    "Player"

  return (
    <div className="console-texture relative flex min-h-screen flex-col overflow-hidden bg-[color:var(--background)]">
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <header className="sticky top-0 z-30 border-b border-[color:var(--border)] bg-[color:var(--background)]/96 shadow-[0_18px_70px_-54px_rgba(17,16,39,0.78)] backdrop-blur-xl">
        <div className="mx-auto flex h-16 w-full max-w-[1360px] items-center justify-between px-5 sm:px-6 lg:px-8">
          <Link
            to="/host"
            className="group inline-flex items-center gap-3 rounded-xl text-sm font-semibold tracking-tight focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--background)]"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] shadow-[inset_0_1px_0_rgba(255,255,255,0.92),0_10px_28px_-22px_rgba(17,16,39,0.75)]">
              <WildGuessLogo size={22} />
            </span>
            <span className="flex items-center gap-2">
              <span className="font-display text-base font-bold leading-none transition-colors group-hover:text-[color:var(--primary)]">
                Wild Guess Trivia
              </span>
              <LiveDot reduced={!!reduced} />
            </span>
          </Link>

          <div className="flex items-center gap-2">
            <motion.span
              whileHover={reduced ? undefined : { y: -1 }}
              whileTap={reduced ? undefined : { scale: 0.97 }}
              transition={{ type: "spring", stiffness: 360, damping: 22 }}
              className="inline-flex"
            >
              <span
                className="hidden rounded-full border border-[color:var(--border)] bg-[color:var(--surface)] px-3 py-1.5 text-xs font-bold text-[color:var(--foreground)] shadow-[inset_0_1px_0_rgba(255,255,255,0.88)] transition-colors hover:bg-[color:var(--surface-soft)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--background)] sm:inline-flex"
              >
                {displayName}
              </span>
            </motion.span>

            <ThemeToggle />

            <motion.button
              onClick={handleLogout}
              whileHover={reduced ? undefined : { y: -1 }}
              whileTap={reduced ? undefined : { scale: 0.96 }}
              transition={{ type: "spring", stiffness: 360, damping: 22 }}
              className="rounded-full border border-transparent px-3 py-1.5 text-xs font-bold text-[color:var(--muted-foreground)] transition-colors hover:border-[color:var(--border)] hover:bg-[color:var(--surface-soft)] hover:text-[color:var(--foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--background)]"
            >
              Sign out
            </motion.button>
          </div>
        </div>
      </header>
      <main
        id="main-content"
        tabIndex={-1}
        aria-label="Wild Guess console"
        className="relative z-[1] mx-auto w-full max-w-[1360px] flex-1 px-5 py-6 focus:outline-none sm:px-6 lg:px-8"
      >
        <Outlet />
      </main>
    </div>
  )
}

/**
 * Tiny pulsing lime dot next to the wordmark — visual cue that this is a
 * live-show control surface. Disabled under reduced motion.
 */
function LiveDot({ reduced }: { reduced: boolean }) {
  return (
    <span className="relative inline-flex h-2 w-2 items-center justify-center" aria-hidden="true">
      {!reduced && (
        <motion.span
          className="absolute inset-0 rounded-full bg-[color:var(--accent)]"
          initial={{ opacity: 0.6, scale: 1 }}
          animate={{ opacity: 0, scale: 2.4 }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeOut" }}
        />
      )}
      <span className="relative inline-block h-2 w-2 rounded-full bg-[color:var(--accent)] shadow-[0_0_8px_var(--accent)]" />
    </span>
  )
}
